import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useApp } from "../context/AppContext";
import {
  projectNoMembership,
  projectWithMembership,
} from "../lib/calculations";
import ResultCard from "./ResultCard";

const slide = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

const LanguageStep = ({ onNext }) => {
  const { t, setLanguage } = useApp();
  const choose = (lang) => {
    setLanguage(lang);
    onNext();
  };
  return (
    <motion.div key="lang" {...slide} className="text-center">
      <p className="mb-4 text-xs tracking-luxury text-[#D4AF37]">
        {t("step")} 01
      </p>
      <h2 className="font-display text-2xl text-white sm:text-4xl">
        {t("q_language")}
      </h2>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => choose("es")}
          className="group border border-white/10 bg-white/[0.03] px-8 py-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-white/[0.06]"
          data-testid="step-lang-es"
        >
          <div className="font-display text-3xl text-white group-hover:text-[#D4AF37]">
            Español
          </div>
          <div className="mt-2 text-xs uppercase tracking-luxury text-white/40">
            ES
          </div>
        </button>
        <button
          type="button"
          onClick={() => choose("en")}
          className="group border border-white/10 bg-white/[0.03] px-8 py-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-white/[0.06]"
          data-testid="step-lang-en"
        >
          <div className="font-display text-3xl text-white group-hover:text-[#D4AF37]">
            English
          </div>
          <div className="mt-2 text-xs uppercase tracking-luxury text-white/40">
            EN
          </div>
        </button>
      </div>
    </motion.div>
  );
};

const MembershipStep = ({ onSelect }) => {
  const { t } = useApp();
  return (
    <motion.div key="membership" {...slide} className="text-center">
      <p className="mb-4 text-xs tracking-luxury text-[#D4AF37]">
        {t("step")} 02
      </p>
      <h2 className="font-display text-2xl leading-tight text-white sm:text-4xl">
        {t("q_membership")}
      </h2>
      <div className="mt-10 grid grid-cols-1 gap-4">
        <button
          type="button"
          onClick={() => onSelect("no_membership")}
          className="group border border-white/10 bg-white/[0.03] px-8 py-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-white/[0.06]"
          data-testid="step-membership-no"
        >
          <div className="font-display text-xl text-white group-hover:text-[#D4AF37] sm:text-2xl">
            {t("opt_no_membership")}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onSelect("with_membership")}
          className="group border border-white/10 bg-white/[0.03] px-8 py-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-white/[0.06]"
          data-testid="step-membership-yes"
        >
          <div className="font-display text-xl text-white group-hover:text-[#D4AF37] sm:text-2xl">
            {t("opt_with_membership")}
          </div>
        </button>
      </div>
    </motion.div>
  );
};

const NumberField = ({ label, value, onChange, unit, hint, testid }) => (
  <div className="text-center" data-testid={`${testid}-wrapper`}>
    <label className="block font-display text-xl text-white sm:text-2xl">
      {label}
    </label>
    {hint && (
      <p className="mt-2 text-xs uppercase tracking-wider text-white/40">{hint}</p>
    )}
    <div className="mt-8 flex items-end justify-center gap-3">
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-44 border-0 border-b-2 border-white/15 bg-transparent px-0 py-3 text-center font-display text-4xl text-white outline-none transition-colors focus:border-[#D4AF37] sm:w-48 sm:text-5xl"
        data-testid={testid}
      />
      <span className="pb-3 text-xs uppercase tracking-luxury text-white/40">
        {unit}
      </span>
    </div>
  </div>
);

const NoMembershipForm = ({ values, setValues, onNext }) => {
  const { t } = useApp();
  const [step, setStep] = useState(0);
  const fields = [
    {
      label: t("q_years"),
      hint: t("q_years_hint"),
      unit: t("unit_years"),
      key: "years",
      testid: "input-years",
    },
    {
      label: t("q_nights"),
      unit: t("unit_nights"),
      key: "nights",
      testid: "input-nights",
    },
    {
      label: t("q_price"),
      unit: t("unit_usd"),
      key: "price",
      testid: "input-price",
    },
  ];
  const f = fields[step];
  const canNext = values[f.key] !== "" && Number(values[f.key]) > 0;
  return (
    <motion.div key={`nm-${step}`} {...slide} className="text-center">
      <p className="mb-4 text-xs tracking-luxury text-[#D4AF37]">
        {t("step")} 0{3 + step}
      </p>
      <NumberField
        label={f.label}
        value={values[f.key]}
        onChange={(v) => setValues({ ...values, [f.key]: v })}
        unit={f.unit}
        hint={f.hint}
        testid={f.testid}
      />
      <div className="mt-12 flex items-center justify-center gap-6">
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            className="text-xs uppercase tracking-luxury text-white/50 hover:text-white"
            data-testid="form-back-btn"
          >
            ← {t("btn_back")}
          </button>
        )}
        <button
          type="button"
          disabled={!canNext}
          onClick={() => (step < fields.length - 1 ? setStep(step + 1) : onNext())}
          className="bg-[#D4AF37] px-10 py-4 text-xs font-bold uppercase tracking-luxury text-black transition-all duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-25"
          data-testid="form-next-btn"
        >
          {t("btn_next")}
        </button>
      </div>
    </motion.div>
  );
};

const WithMembershipForm = ({ values, setValues, onNext }) => {
  const { t } = useApp();
  const canNext = values.fee !== "" && Number(values.fee) > 0;
  return (
    <motion.div key="wm" {...slide} className="text-center">
      <p className="mb-4 text-xs tracking-luxury text-[#D4AF37]">
        {t("step")} 03
      </p>
      <NumberField
        label={t("q_fee")}
        value={values.fee}
        onChange={(v) => setValues({ ...values, fee: v })}
        unit={t("unit_usd")}
        testid="input-fee"
      />
      <div className="mt-12">
        <button
          type="button"
          disabled={!canNext}
          onClick={onNext}
          className="bg-[#D4AF37] px-10 py-4 text-xs font-bold uppercase tracking-luxury text-black transition-all duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-25"
          data-testid="form-next-btn"
        >
          {t("btn_next")}
        </button>
      </div>
    </motion.div>
  );
};

const Calculator = ({ initialStep = "lang" }) => {
  const { language, settings } = useApp();
  const [step, setStep] = useState(language ? "membership" : initialStep);
  const [scenario, setScenario] = useState(null);
  const [noMemValues, setNoMemValues] = useState({
    years: "",
    nights: "",
    price: "",
  });
  const [withMemValues, setWithMemValues] = useState({ fee: "" });
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep(language ? "membership" : "lang");
    setScenario(null);
    setNoMemValues({ years: "", nights: "", price: "" });
    setWithMemValues({ fee: "" });
    setResult(null);
  };

  const compute = (scenarioType) => {
    const inflationRate = settings.inflation_rate;
    const endYear = settings.end_year;
    if (scenarioType === "no_membership") {
      const proj = projectNoMembership({
        pricePerNight: Number(noMemValues.price),
        nightsPerYear: Number(noMemValues.nights),
        inflationRate,
        endYear,
      });
      const r = {
        scenario: "no_membership",
        total: proj.total,
        inputs: noMemValues,
        breakdown: proj.breakdown,
        inflationRate,
        endYear,
      };
      setResult(r);
      window.dispatchEvent(new CustomEvent("ourigen:result", { detail: r }));
    } else {
      const proj = projectWithMembership({
        annualFee: Number(withMemValues.fee),
        inflationRate,
        endYear,
      });
      const r = {
        scenario: "with_membership",
        total: proj.total,
        inputs: withMemValues,
        breakdown: proj.breakdown,
        baseFee: Number(withMemValues.fee),
        inflationRate,
        endYear,
      };
      setResult(r);
      window.dispatchEvent(new CustomEvent("ourigen:result", { detail: r }));
    }
    setStep("result");
  };

  return (
    <section
      id="calculator"
      className="ourigen-pattern relative py-16 sm:py-24"
      data-testid="calculator-section"
    >
      <div className="mx-auto max-w-2xl px-5 sm:px-8">
        <div className="border border-white/10 bg-[#131316]/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.5)] backdrop-blur-md sm:p-12">
          <AnimatePresence mode="wait">
            {step === "lang" && (
              <LanguageStep onNext={() => setStep("membership")} />
            )}
            {step === "membership" && (
              <MembershipStep
                onSelect={(s) => {
                  setScenario(s);
                  setStep(s === "no_membership" ? "form-nm" : "form-wm");
                }}
              />
            )}
            {step === "form-nm" && (
              <NoMembershipForm
                values={noMemValues}
                setValues={setNoMemValues}
                onNext={() => compute("no_membership")}
              />
            )}
            {step === "form-wm" && (
              <WithMembershipForm
                values={withMemValues}
                setValues={setWithMemValues}
                onNext={() => compute("with_membership")}
              />
            )}
            {step === "result" && (
              <ResultCard result={result} onReset={reset} />
            )}
          </AnimatePresence>

          {step !== "lang" && step !== "result" && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  if (step === "membership") setStep(language ? "membership" : "lang");
                  else setStep("membership");
                }}
                className="flex items-center gap-2 text-xs uppercase tracking-luxury text-white/40 hover:text-white"
                data-testid="calc-back-btn"
              >
                <ChevronLeft size={14} /> {step === "membership" ? "↺" : ""}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Calculator;
